import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Briefcase } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const workspace = params.get("workspace");

  useEffect(() => {
    if (workspace) {
      api.get(`/public/brand/${workspace}`).then(({ data }) => setBrand(data)).catch(() => {});
    }
  }, [workspace]);

  const accent = brand?.primary_color || "#f59e0b";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      const { data } = await api.get("/auth/me");
      setUser(data);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 bg-slate-900 border-r border-slate-800">
        <div className="flex items-center gap-3">
          {brand?.has_logo && workspace ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/brand/${workspace}/logo`}
              alt={brand.name} data-testid="login-brand-logo"
              className="h-10 w-10 rounded-xl object-contain bg-white/10" />
          ) : (
            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: accent }}>
              <Briefcase className="h-5 w-5 text-slate-900" />
            </div>
          )}
          <span className="font-heading font-bold text-white text-xl" data-testid="login-brand-name">
            {brand ? brand.name : "Fieldistry"}</span>
        </div>
        <div>
          {brand ? (
            <>
              <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white tracking-tight leading-tight" data-testid="login-brand-headline">
                {brand.name}<br />
                <span style={{ color: accent }}>{brand.login_tagline}</span>
              </h1>
              <p className="text-slate-400 mt-6 text-lg max-w-md">Sign in to your company workspace.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white tracking-tight leading-tight">
                Enter information once.<br />
                <span style={{ color: accent }}>Update everything.</span>
              </h1>
              <p className="text-slate-400 mt-6 text-lg max-w-md">
                The operations platform for trade, construction and field-service businesses.
                Jobs, crews, materials, purchasing and scheduling, one system.
              </p>
            </>
          )}
        </div>
        <div className="text-slate-600 text-sm">
          {brand && brand.show_powered_by === false
            ? "" : brand ? "Powered by Fieldistry" : "Built for roofing, HVAC, plumbing, landscaping, cleaning and more."}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} data-testid="login-form"
          className="w-full max-w-sm space-y-5 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-slate-900" />
            </div>
            <span className="font-heading font-bold text-white text-lg">{brand ? brand.name : "Fieldistry"}</span>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">Sign in</h2>
            <p className="text-slate-500 text-sm mt-1">Access your workspace</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Email</Label>
            <Input data-testid="login-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-900 border-slate-700 text-white focus-visible:ring-amber-500"
              placeholder="you@company.com" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Password</Label>
            <Input data-testid="login-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-slate-900 border-slate-700 text-white focus-visible:ring-amber-500"
              placeholder="••••••••" />
          </div>
          <Button data-testid="login-form-submit-button" type="submit" disabled={busy}
            className="w-full text-slate-900 font-semibold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: accent }}>
            {busy ? "Signing in..." : "Sign in"}
          </Button>
          {forgot ? (
            <div className="flex gap-2" data-testid="forgot-form">
              <Input data-testid="forgot-email" type="email" placeholder="Your email"
                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white h-9 text-sm" />
              <Button type="button" size="sm" variant="outline" data-testid="forgot-submit"
                className="border-slate-700 text-slate-300 shrink-0"
                onClick={async () => {
                  try {
                    await api.post("/auth/forgot-password", { email: forgotEmail });
                    toast.success("If that email is registered, a reset link is on its way.");
                    setForgot(false);
                  } catch (err) { toast.error(apiError(err)); }
                }}>
                Send link</Button>
            </div>
          ) : (
            <button type="button" data-testid="forgot-toggle" onClick={() => setForgot(true)}
              className="text-sm text-slate-500 hover:text-amber-400 transition-colors w-full text-center">
              Forgot password?
            </button>
          )}
          <p className="text-sm text-slate-500 text-center">
            New organization?{" "}
            <Link to="/onboarding" data-testid="onboarding-link"
              className="text-amber-500 hover:text-amber-400 font-medium">
              Set up your workspace
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
